// File: src/components/artistDashboard/chartingServices/ConfirmPromotionModal.js
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import ChartProjectionGraph from './ChartProjectionGraph';

const ConfirmPromotionModal = ({
  isOpen,
  onClose,
  song,
  serviceName = "Start The Buzz",
  basePrice = 75,
  pointsApplied = 0,
  cashDue = 75,
  isDarkMode = true
}) => {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [isLoadingCards, setIsLoadingCards] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const songRank = typeof song?.position === 'number' && song.position > 0 ? song.position : 0;

  // Fetch saved payment methods from Cloud Functions
  const fetchCards = useCallback(async () => {
    setIsLoadingCards(true);
    try {
      const functions = getFunctions();
      const getSavedPaymentMethods = httpsCallable(functions, 'getSavedPaymentMethods');
      const res = await getSavedPaymentMethods();
      const list = res.data || [];
      setCards(list);
      if (list.length > 0) {
        setSelectedCardId(list[0].id);
      }
    } catch (err) {
      console.warn("Could not load payment methods:", err);
    } finally {
      setIsLoadingCards(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchCards();
    }
  }, [isOpen, fetchCards]);

  if (!isOpen || !song) return null;

  const handleFinalizePurchase = async () => {
    if (cashDue > 0 && !selectedCardId && cards.length === 0) {
      toast.error("Please add a payment card to complete checkout.");
      return;
    }

    setIsProcessing(true);
    try {
      toast.success(`"${serviceName}" confirmed for "${song.title}"!`);
      onClose();
    } catch (error) {
      toast.error(error.message || "Failed to process transaction.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-md"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className={`w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-3xl border p-5 sm:p-6 shadow-2xl space-y-4 transition-colors ${
            isDarkMode 
              ? 'bg-zinc-950 border-zinc-800 text-white' 
              : 'bg-white border-gray-200 text-slate-900 shadow-slate-300/40'
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between pb-3 border-b border-gray-200 dark:border-zinc-800">
            <div>
              <span className="text-[10px] font-black uppercase tracking-widest text-brand-pink">
                Order Review & Impact
              </span>
              <h3 className="text-base sm:text-lg font-black uppercase tracking-wider text-slate-900 dark:text-white">
                Confirm Package
              </h3>
            </div>
            <button 
              onClick={onClose} 
              className="text-gray-400 hover:text-slate-900 dark:hover:text-white text-xl font-bold p-1 leading-none transition-colors"
              aria-label="Close modal"
            >
              &times;
            </button>
          </div>

          {/* Selected Track & Service Summary Card */}
          <div className={`p-3.5 rounded-2xl border flex items-center justify-between gap-3 transition-colors ${
            isDarkMode ? 'bg-zinc-900/80 border-zinc-800' : 'bg-gray-50 border-gray-200'
            }`}>
            <div className="flex items-center gap-3 min-w-0">
                {song.albumArtworkURL && (
                <img
                    src={song.albumArtworkURL}
                    alt={song.title}
                    className="w-12 h-12 rounded-xl object-cover flex-shrink-0 border border-gray-200 dark:border-zinc-700 shadow-sm"
                />
                )}
                <div className="min-w-0">
                <span className="text-[10px] font-black uppercase tracking-wider text-brand-pink block">
                    {serviceName}
                </span>
                <h4 className="text-xs font-black truncate text-slate-900 dark:text-white">
                    "{song.title}"
                </h4>
                {/* Display 'Unranked' when position is 0 */}
                <p className="text-[10px] font-bold text-slate-500 dark:text-zinc-400 mt-0.5">
                    Current Rank: {songRank > 0 ? `#${songRank}` : 'Unranked'}
                </p>
                </div>
            </div>

            <div className="text-right flex-shrink-0">
                <span className="text-xs font-black text-slate-900 dark:text-white block">
                ${basePrice.toFixed(2)} AUD
                </span>
                {pointsApplied > 0 && (
                <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 block">
                    -{pointsApplied.toLocaleString()} pts (-${(pointsApplied / 1000).toFixed(2)})
                </span>
                )}
                <span className="text-xs font-black text-[#ff00cc] block mt-0.5">
                Due: ${cashDue.toFixed(2)} AUD
                </span>
            </div>
        </div>

          {/* 6-Week Forecasted Trajectory Graph */}
          <div>
            <ChartProjectionGraph
              currentRank={song.position || 50}
              packageBoostPoints={basePrice}
              isDarkMode={isDarkMode}
            />
          </div>

          {/* Payment Method / Saved Cards */}
          <div className={`p-3.5 rounded-2xl border space-y-2.5 transition-colors ${
            isDarkMode 
              ? 'bg-zinc-900 border-zinc-800' 
              : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-900 dark:text-white">
                Payment Method
              </span>
              <button
                type="button"
                onClick={() => navigate('/add-payment-method')}
                className="text-[10px] font-bold text-brand-pink hover:underline"
              >
                + Alter / Add Card
              </button>
            </div>

            {isLoadingCards ? (
              <p className="text-xs text-slate-500 dark:text-zinc-400 animate-pulse">Checking saved cards...</p>
            ) : cards.length > 0 ? (
              <div className="space-y-1.5">
                {cards.map((card) => (
                  <label
                    key={card.id}
                    className={`flex items-center justify-between p-2.5 rounded-xl border cursor-pointer text-xs transition-all ${
                      selectedCardId === card.id
                        ? 'border-blue-500 bg-blue-500/10 text-slate-900 dark:text-white font-bold'
                        : isDarkMode
                          ? 'border-zinc-800 bg-zinc-950/40 text-zinc-400 hover:text-white'
                          : 'border-gray-200 bg-white text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="selectedCard"
                        checked={selectedCardId === card.id}
                        onChange={() => setSelectedCardId(card.id)}
                        className="text-blue-500 focus:ring-0"
                      />
                      <span className="capitalize">{card.brand} •••• {card.last4}</span>
                    </div>
                    <span className="text-[10px] opacity-70">
                      {String(card.exp_month).padStart(2, '0')}/{card.exp_year}
                    </span>
                  </label>
                ))}
              </div>
            ) : (
              <div className="text-center py-2">
                <p className="text-xs text-slate-500 dark:text-zinc-400 mb-2">No saved card found on file.</p>
                <button
                  type="button"
                  onClick={() => navigate('/add-payment-method')}
                  className="px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-black dark:bg-zinc-800 dark:hover:bg-zinc-700 text-white text-xs font-bold transition-all"
                >
                  Add Card Now
                </button>
              </div>
            )}
          </div>

          {/* Action Button */}
          <button
            type="button"
            disabled={isProcessing}
            onClick={handleFinalizePurchase}
            className="w-full bg-[#ff00cc] hover:bg-[#ff00cc]/90 text-white font-black text-xs sm:text-sm py-3.5 rounded-2xl shadow-lg shadow-[#ff00cc]/25 active:scale-[0.99] transition-all disabled:opacity-50"
          >
            {isProcessing ? "Processing..." : `Confirm & Pay $${cashDue.toFixed(2)} AUD`}
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ConfirmPromotionModal;